import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { formatMinor } from '../../lib/format';
import { BarChart, LineChart } from '../../components/charts';
import { Card, FormField, PageHeader, Select, Spinner } from '../../components/ui';

export function ReportsPage() {
  const [granularity, setGranularity] = useState<'day' | 'week' | 'month'>('day');

  const reportQuery = useQuery({
    queryKey: ['sales-report', granularity],
    queryFn: () => api.reports.sales({ granularity }),
  });

  const report = reportQuery.data;
  const grossPoints =
    report?.points.map((p) => ({ label: p.bucket, value: p.grossMinor / 100 })) ?? [];
  const ticketPoints = report?.points.map((p) => ({ label: p.bucket, value: p.ticketsSold })) ?? [];

  return (
    <div>
      <PageHeader
        title="Sales reports"
        description="Gross/net revenue and tickets sold over time."
      />
      <Card className="mb-6">
        <FormField label="Granularity">
          <Select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as 'day' | 'week' | 'month')}
          >
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </Select>
        </FormField>
      </Card>
      {reportQuery.isLoading ? (
        <Spinner />
      ) : reportQuery.error ? (
        <ErrorSummary error={toParsedError(reportQuery.error)} />
      ) : report ? (
        <>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card title="Gross">{formatMinor(report.totals.grossMinor, report.currency)}</Card>
            <Card title="Net">{formatMinor(report.totals.netMinor, report.currency)}</Card>
            <Card title="Tickets sold">{report.totals.ticketsSold}</Card>
            <Card title="Orders">{report.totals.ordersCount}</Card>
          </div>
          <Card title="Gross revenue" className="mb-6">
            <BarChart points={grossPoints} />
          </Card>
          <Card title="Tickets sold">
            <LineChart points={ticketPoints} />
          </Card>
        </>
      ) : null}
    </div>
  );
}
